import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = {
  class: "mb-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-left",
  "data-testid": "schedule-requirements-registration-block"
}
const _hoisted_3 = { class: "flex flex-wrap items-center gap-2 text-sm font-semibold text-textPrimary" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", null, "Registro de plantão: gerenciado pela política de registro", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("span", { class: "rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-normal text-gray-600" }, " somente leitura ", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", { class: "mt-1.5 text-xs text-gray-600" }, " Exigência de check, público (nosso time / externos) e método de check são definidos na política de registro de plantão vigente desta escala. ", -1)
const _hoisted_7 = {
  class: "mt-1.5 text-xs text-gray-600",
  "data-testid": "schedule-requirements-registration-summary"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("strong", null, "Hoje:", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("hr", { class: "border-t border-gray-300 my-2 px-2" }, null, -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("hr", { class: "border-t border-gray-300 my-2 px-2" }, null, -1)

import { ref, computed, onMounted } from 'vue'
import { mdiLock } from '@mdi/js'
import SVGIcon from '@/components/SVGIcon.vue'
import EditableItemGroup from '../../../menu/EditableItemGroup.vue'
import EditableLevelGroup from '../../../menu/EditableLevelGroup.vue'
import { PositionResponse } from '@/Network/Types/Responses/PositionResponses'
import { ShiftResponse } from '@/Network/Types/Responses/ShiftResponses'
import { FormationResponse } from '@/Network/Types/Responses/FormationResponses'
import Network from '@/Network'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import Spinner from '@/components/others/Spinner.vue'
import type { DSAttendancePolicyData } from '@/types/ds-attendance.types'
import {
  resolveCurrentCheckRequirement,
  buildScheduleCheckSummary,
  type ScheduleCheckFlags
} from '@/utils/registrationCheck'

type Key = 'positions' | 'shifts' | 'formations'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleRequirementsTab',
  props: {
    scheduleId: {}
  },
  emits: ["update:isSaving", "open-policy-tab"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;

const toast = useToast()
const { notifyError } = useErrorToast()





const activePositions = ref<PositionResponse[]>([])
const inactivePositions = ref<PositionResponse[]>([])
const activeShifts = ref<ShiftResponse[]>([])
const inactiveShifts = ref<ShiftResponse[]>([])
const activeFormations = ref<FormationResponse[]>([])
const inactiveFormations = ref<FormationResponse[]>([])
const requiresDoctorPaymentFormationRef = ref<boolean>(false)
const isLoading = ref(true)
const hasUnsavedChanges = ref(false)

/** Insumos do bloco somente-leitura do registro de plantão (M7, Tela 4). */
const registrationPolicies = ref<DSAttendancePolicyData[]>([])
const scheduleCheckFlags = ref<ScheduleCheckFlags | null>(null)

/** `política ?? escala`: a flag legada só responde enquanto não há política vigente. */
const currentCheckRequirement = computed(() =>
  resolveCurrentCheckRequirement(registrationPolicies.value, scheduleCheckFlags.value)
)
const registrationSummary = computed(() => buildScheduleCheckSummary(currentCheckRequirement.value))

/**
 * Leitura TOLERANTE A FALHA: o bloco somente-leitura é informativo e não pode derrubar
 * a aba de Requisitos nem disparar toast. Sem políticas, o resumo cai nas flags da escala.
 */
const loadRegistrationPolicies = async () => {
  try {
    registrationPolicies.value = await Network.dsAttendance.listPolicies(props.scheduleId)
  } catch {
    registrationPolicies.value = []
  }
}

const loadRequirements = async () => {
  isLoading.value = true
  const registrationPoliciesLoad = loadRegistrationPolicies()
  try {
    const [positions, shifts, formations, schedule] = await Promise.all([
      Network.position.getByScheduleId(props.scheduleId),
      Network.shift.getByScheduleId(props.scheduleId),
      Network.paymentFormation.getByScheduleId(props.scheduleId),
      Network.schedule.getById(props.scheduleId)
    ])

    activePositions.value = positions.data.filter((p) => p.isActive)
    inactivePositions.value = positions.data.filter((p) => !p.isActive)
    activeShifts.value = shifts.data.filter((s) => s.isActive)
    inactiveShifts.value = shifts.data.filter((s) => !s.isActive)
    activeFormations.value = formations.data.filter((f) => f.isActive)
    inactiveFormations.value = formations.data.filter((f) => !f.isActive)
    requiresDoctorPaymentFormationRef.value = schedule.requiresDoctorPaymentFormation
    scheduleCheckFlags.value = {
      ourTeam: Boolean(schedule.requiredCheckOurTeam),
      notOurTeam: Boolean(schedule.requiredCheckNotOurTeam)
    }
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os requisitos da escala.')
  } finally {
    await registrationPoliciesLoad
    isLoading.value = false
  }
}

onMounted(() => {
  loadRequirements()
})

function onToggle(key: Key, index: number, makeActive: boolean) {
  const src = makeActive ? inactiveMap[key] : activeMap[key]
  const dst = makeActive ? activeMap[key] : inactiveMap[key]
  const item = src.value.splice(index, 1)[0]
  dst.value.push({ ...item, isActive: makeActive })
  hasUnsavedChanges.value = true
}

function onUpdateRequiresDoctorPaymentFormation(value: boolean) {
  requiresDoctorPaymentFormationRef.value = value
  hasUnsavedChanges.value = true
}

function onRename(key: Key, index: number, newName: string) {
  const item = mapByKey(key)[index]
  if (item.name === newName) return

  item.name = newName.trim()
  hasUnsavedChanges.value = true
}

function onCreate(key: Key, newName: string) {
  const newItem = { name: newName, isActive: true }
  activeMap[key].value.push(newItem as any)
  hasUnsavedChanges.value = true
}

const activeMap: Record<Key, any> = {
  positions: activePositions,
  shifts: activeShifts,
  formations: activeFormations
}

const inactiveMap: Record<Key, any> = {
  positions: inactivePositions,
  shifts: inactiveShifts,
  formations: inactiveFormations
}

function mapByKey(key: Key) {
  return key === 'positions'
    ? activePositions.value.concat(inactivePositions.value)
    : key === 'shifts'
      ? activeShifts.value.concat(inactiveShifts.value)
      : activeFormations.value.concat(inactiveFormations.value)
}

const handleSave = async () => {
  if (requiresDoctorPaymentFormationRef.value && activeFormations.value.length === 0) {
    toast.error(
      'Para salvar com a opção de formação habilitada, você deve ter ao menos uma formação ativa.'
    )
    return
  }

  emit('update:isSaving', true)

  try {
    await Promise.all([
      Network.position.updateBulk(props.scheduleId, [
        ...activePositions.value,
        ...inactivePositions.value
      ]),
      Network.shift.updateBulk(props.scheduleId, [...activeShifts.value, ...inactiveShifts.value]),
      Network.paymentFormation.updateBulk(props.scheduleId, [
        ...activeFormations.value,
        ...inactiveFormations.value
      ]),
      Network.schedule.update(props.scheduleId, {
        requiresDoctorPaymentFormation: requiresDoctorPaymentFormationRef.value
      })
    ])

    hasUnsavedChanges.value = false
    toast.success('Requisitos salvos com sucesso.')
  } catch (e: unknown) {
    // Os `updateBulk` de posição/turno/formação e o `schedule.update` normalizam
    // o erro em ServiceError, então `axios.isAxiosError` deixou de casar e a
    // mensagem do backend se perdia no fallback fixo.
    notifyError(e, 'Não foi possível salvar os requisitos da escala.')
  } finally {
    emit('update:isSaving', false)
  }
}

__expose({
  handleSave,
  hasUnsavedChanges
})

return (_ctx: any,_cache: any) => {
  return (isLoading.value)
    ? (_openBlock(), _createBlock(Spinner, {
        key: 0,
        color: "primary",
        size: "5rem",
        width: "0.5rem",
        class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
      }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h4", _hoisted_3, [
            _createVNode(SVGIcon, {
              path: _unref(mdiLock),
              class: "w-4 h-4 shrink-0 text-gray-500"
            }, null, 8, ["path"]),
            _hoisted_4,
            _hoisted_5
          ]),
          _hoisted_6,
          _createElementVNode("p", _hoisted_7, [
            _hoisted_8,
            _createTextVNode(" " + _toDisplayString(registrationSummary.value), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "mt-2.5 text-xs font-semibold text-primary30 hover:underline",
            "data-testid": "schedule-requirements-registration-link",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('open-policy-tab')))
          }, " Ver política ")
        ]),
        _createVNode(EditableItemGroup, {
          title: "Posições",
          itemLabel: "Nova posição",
          itemActiveLabel: "Ativar posição",
          createButtonLabel: 'Criar Posição',
          type: 'positions',
          items: activePositions.value.map((p) => p.name),
          inactiveItems: inactivePositions.value.map((p) => p.name),
          onUpdate: _cache[1] || (_cache[1] = (i, val) => onRename('positions', i, val)),
          onDeactivate: _cache[2] || (_cache[2] = (i) => onToggle('positions', i, false)),
          onReactivate: _cache[3] || (_cache[3] = (i) => onToggle('positions', i, true)),
          onCreate: _cache[4] || (_cache[4] = (newName) => onCreate('positions', newName))
        }, null, 8, ["items", "inactiveItems"]),
        _hoisted_9,
        _createVNode(EditableItemGroup, {
          title: "Turnos",
          itemLabel: "Novo turno",
          itemActiveLabel: "Ativar turno",
          createButtonLabel: 'Criar Turno',
          type: 'shifts',
          items: activeShifts.value.map((s) => s.name),
          inactiveItems: inactiveShifts.value.map((s) => s.name),
          onUpdate: _cache[5] || (_cache[5] = (i, val) => onRename('shifts', i, val)),
          onDeactivate: _cache[6] || (_cache[6] = (i) => onToggle('shifts', i, false)),
          onReactivate: _cache[7] || (_cache[7] = (i) => onToggle('shifts', i, true)),
          onCreate: _cache[8] || (_cache[8] = (newName) => onCreate('shifts', newName))
        }, null, 8, ["items", "inactiveItems"]),
        _hoisted_10,
        _createVNode(EditableLevelGroup, {
          title: "Níveis de Formação",
          itemLabel: "Nova formação",
          createButtonLabel: 'Criar Formação',
          type: 'formations',
          items: activeFormations.value.map((f) => f.name),
          inactiveItems: inactiveFormations.value.map((f) => f.name),
          requiresDoctorPaymentFormation: requiresDoctorPaymentFormationRef.value,
          onUpdate: _cache[9] || (_cache[9] = (i, val) => onRename('formations', i, val)),
          onDeactivate: _cache[10] || (_cache[10] = (i) => onToggle('formations', i, false)),
          onReactivate: _cache[11] || (_cache[11] = (i) => onToggle('formations', i, true)),
          onCreate: _cache[12] || (_cache[12] = (newName) => onCreate('formations', newName)),
          onUpdateRequiresDoctorPaymentFormation: onUpdateRequiresDoctorPaymentFormation
        }, null, 8, ["items", "inactiveItems", "requiresDoctorPaymentFormation"])
      ]))
}
}

})